import React, { ReactElement } from 'react';
import { Control, DeepMap, FieldError, useForm } from 'react-hook-form';
import TranslatedText from 'components/atoms/translated_text';
import {
  AppDatePicker,
  AppSelect,
  AppTextField,
} from 'components/molecules/form_controls';
import GridLayout from 'components/molecules/grid_layout';
import { Button } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import VatRateActions from 'redux/reducers/vat_rate_reducer/actions';
import * as VatRateSelectors from 'redux/reducers/vat_rate_reducer/selectors';
import * as DateUtils from 'utils/dates';
import { Languages } from 'config';
import useStyles from './styles';
import { StateStatus } from 'redux/utils/common';

const Translations = {
  country_control: {
    [Languages.Greek]: 'Country',
    [Languages.English]: 'Country',
  },
  vat_rate_type_control: {
    [Languages.Greek]: 'VAT Rate Type',
    [Languages.English]: 'VAT Rate Type',
  },
  vat_rate_control: {
    [Languages.Greek]: 'VAT Rate (%)',
    [Languages.English]: 'VAT Rate (%)',
  },
  valid_from_control: {
    [Languages.Greek]: 'Valid From',
    [Languages.English]: 'Valid From',
  },
  valid_to_control: {
    [Languages.Greek]: 'Valid To',
    [Languages.English]: 'Valid To',
  },
  submit: {
    [Languages.Greek]: 'Save',
    [Languages.English]: 'Save',
  },
};

interface IProps {
  vat_rate_id: number;
  vat_rate_type_id: number;
  country_code: string;
  country_name: string;
  vat_rate: number;
  valid_from: string;
  valid_to: string | null;
}

interface FormFields {
  country: string;
  vat_rate_type_id: string;
  vat_rate: string;
  valid_from: Date | null;
  valid_to: Date | null;
}

export default function EditVatRateForm(props: IProps): ReactElement {
  const dispatch = useDispatch();
  const classes = useStyles();
  const vatRateTypes = VatRateSelectors.useGetVatRateTypes();

  const typeOptions =
    vatRateTypes.status === StateStatus.Success && vatRateTypes.data
      ? vatRateTypes.data.map((t: any) => ({
          id: String(t.vat_rate_type_id),
          label: t.description,
        }))
      : [];

  const defaultValues: FormFields = {
    country: `${props.country_code} - ${props.country_name}`,
    vat_rate_type_id: props.vat_rate_type_id != null ? String(props.vat_rate_type_id) : '',
    vat_rate: props.vat_rate != null ? String(props.vat_rate) : '',
    valid_from: props.valid_from ? new Date(props.valid_from) : null,
    valid_to: props.valid_to ? new Date(props.valid_to) : null,
  };

  const { errors, control, handleSubmit } = useForm<FormFields>({ defaultValues });

  const onSubmit = (data: FormFields) => {
    dispatch(
      VatRateActions.updateVatRateAction({
        vat_rate_id: props.vat_rate_id,
        vat_rate_type_id: parseInt(data.vat_rate_type_id),
        vat_rate: parseFloat(data.vat_rate),
        valid_from: DateUtils.DateInQueryFormat(data.valid_from as Date),
        valid_to: data.valid_to ? DateUtils.DateInQueryFormat(data.valid_to) : null,
      })
    );
  };

  return (
    <form className={classes.form} noValidate onSubmit={handleSubmit(onSubmit)}>
      <GridLayout
        elements={[
          {
            element: (
              <AppTextField
                variant="outlined"
                margin="normal"
                fullWidth
                id="country"
                label={<TranslatedText textMap={Translations.country_control} />}
                name="country"
                control={control}
                rules={{}}
                disabled
              />
            ),
            size: 12,
            id: 'country',
          },
          {
            element: (
              <AppSelect
                variant="outlined"
                margin="normal"
                fullWidth
                required
                id="vat_rate_type_id"
                label={<TranslatedText textMap={Translations.vat_rate_type_control} />}
                error={'vat_rate_type_id' in errors}
                name="vat_rate_type_id"
                control={control}
                rules={{ required: 'Required Field' }}
                options={typeOptions}
                helperText={errors.vat_rate_type_id && errors.vat_rate_type_id.message}
              />
            ),
            size: 12,
            id: 'vat_rate_type_id',
          },
          {
            element: (
              <AppTextField
                variant="outlined"
                margin="normal"
                fullWidth
                id="vat_rate"
                label={<TranslatedText textMap={Translations.vat_rate_control} />}
                error={'vat_rate' in errors}
                name="vat_rate"
                control={control}
                rules={{
                  required: 'Required Field',
                  pattern: {
                    value: /^\d+(\.\d{1,2})?$/,
                    message: 'Enter a valid number (e.g. 20 or 20.50)',
                  },
                }}
                helperText={errors.vat_rate && errors.vat_rate.message}
              />
            ),
            size: 12,
            id: 'vat_rate',
          },
          {
            element: (
              <AppDatePicker
                fullWidth
                margin="normal"
                label={<TranslatedText textMap={Translations.valid_from_control} />}
                error={'valid_from' in errors}
                control={control}
                required
                rules={{ required: 'Required Field' }}
                autoOk
                name="valid_from"
                helperText={errors.valid_from && errors.valid_from.message}
              />
            ),
            size: 12,
            id: 'valid_from',
          },
          {
            element: (
              <AppDatePicker
                fullWidth
                margin="normal"
                label={<TranslatedText textMap={Translations.valid_to_control} />}
                error={'valid_to' in errors}
                control={control}
                rules={{}}
                autoOk
                name="valid_to"
                helperText={errors.valid_to && errors.valid_to.message}
              />
            ),
            size: 12,
            id: 'valid_to',
          },
          {
            element: (
              <Button
                variant="contained"
                color="primary"
                type="submit"
                style={{ width: '100%' }}
              >
                <TranslatedText textMap={Translations.submit} />
              </Button>
            ),
            size: 12,
            id: 'action',
          },
        ]}
      />
    </form>
  );
}
